import supabase from "../config/supabase";

/**
 * Thumbs up on service listings.
 *
 * The public count is read from services.like_count, which a trigger keeps in
 * step (migration 030). These calls only deal with the current member's own
 * likes, because that is all the row policy exposes.
 */
const serviceLikesApi = {
  /**
   * Which of these listings has the signed-in member already liked?
   * @param {string[]} serviceIds
   * @returns {Promise<{success: boolean, data?: string[], error?: string}>}
   */
  async mine(serviceIds = []) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { success: true, data: [] };

    let query = supabase.from("service_likes").select("service_id");
    // Skip the filter for an unbounded load; the policy limits it to this
    // member's own rows either way.
    if (serviceIds.length) query = query.in("service_id", serviceIds);

    const { data, error } = await query;
    if (error) return { success: false, error: error.message };
    return { success: true, data: (data || []).map((row) => row.service_id) };
  },

  /**
   * Add or remove this member's like.
   * @param {string} serviceId
   * @param {boolean} liked - the state being moved to
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async set(serviceId, liked) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Please sign in to do that." };

    if (!liked) {
      const { error } = await supabase
        .from("service_likes")
        .delete()
        .eq("service_id", serviceId)
        .eq("user_id", user.id);
      if (error) return { success: false, error: error.message };
      return { success: true };
    }

    const { error } = await supabase
      .from("service_likes")
      .insert({ service_id: serviceId, user_id: user.id });

    // Already liked on another device: the end state is what was asked for.
    if (error && error.code === "23505") return { success: true };
    if (error) return { success: false, error: error.message };
    return { success: true };
  },
};

export default serviceLikesApi;
